import { getFunctions, httpsCallable } from "firebase/functions";
import app from "../firebase";
import { supabase } from "../lib/supabase";
import { normalizeTier, type CanonicalTier, RECURRING_PERSONAL_TIERS } from "../types/tiers";

const functions = getFunctions(app);

interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

interface VerifySessionResponse {
  success: boolean;
  priceType: string;
  tier?: string;
  durationMs: number;
  expiresAt: number | null;
  status?: string;
}

interface CancelSubscriptionResponse {
  success: boolean;
  alreadyCancelled: boolean;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: number | null;
}

interface BillingPortalResponse {
  url: string;
}

async function getAccessToken(): Promise<string> {
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session?.access_token) {
    throw new Error("Sign in is required for billing.");
  }
  return data.session.access_token;
}

const createCheckoutSessionFn = httpsCallable<
  {
    priceType: string;
    successUrl: string;
    cancelUrl: string;
    accessToken: string;
  },
  CheckoutSessionResponse
>(functions, "createCheckoutSession");

const verifyCheckoutSessionFn = httpsCallable<
  { sessionId: string; accessToken: string },
  VerifySessionResponse
>(functions, "verifyCheckoutSession");

const cancelSubscriptionFn = httpsCallable<
  { accessToken: string },
  CancelSubscriptionResponse
>(functions, "cancelSubscription");

const createBillingPortalSessionFn = httpsCallable<
  { accessToken: string; returnUrl: string },
  BillingPortalResponse
>(functions, "createBillingPortalSession");

export const RECURRING_SUBSCRIPTION_TIERS: CanonicalTier[] = [...RECURRING_PERSONAL_TIERS];

export type PersonalCheckoutPlan = "day_pass" | "monthly_pro" | "annual_pro";

/**
 * Creates a Stripe checkout session and redirects to payment page.
 * Requires a signed-in Supabase session.
 */
export const initiateCheckout = async (priceType: PersonalCheckoutPlan): Promise<void> => {
  const accessToken = await getAccessToken();
  const currentUrl = window.location.origin;
  const plan = normalizeTier(priceType);

  if (plan !== "day_pass" && plan !== "monthly_pro" && plan !== "annual_pro") {
    throw new Error("Unsupported personal checkout plan.");
  }

  const result = await createCheckoutSessionFn({
    priceType: plan,
    successUrl: `${currentUrl}/success`,
    cancelUrl: currentUrl,
    accessToken,
  });

  if (result.data.url) {
    window.location.href = result.data.url;
  } else {
    throw new Error("Failed to get checkout URL");
  }
};

/**
 * Verifies a completed checkout session. Server persists entitlement;
 * client should sync from Supabase afterward.
 */
export const verifyPayment = async (
  sessionId: string
): Promise<{
  tier: CanonicalTier;
  durationMs: number;
  expiresAt: number | null;
  status?: string;
}> => {
  const accessToken = await getAccessToken();
  const result = await verifyCheckoutSessionFn({ sessionId, accessToken });

  return {
    tier: normalizeTier(result.data.tier || result.data.priceType),
    durationMs: result.data.durationMs,
    expiresAt: result.data.expiresAt,
    status: result.data.status,
  };
};

/**
 * Cancels the signed-in user's personal Stripe subscription at period end.
 */
export const cancelSubscription = async (): Promise<CancelSubscriptionResponse> => {
  const accessToken = await getAccessToken();
  const result = await cancelSubscriptionFn({ accessToken });
  return result.data;
};

/**
 * Opens the Stripe customer billing portal for the signed-in user.
 */
export const openBillingPortal = async (): Promise<void> => {
  const accessToken = await getAccessToken();
  const result = await createBillingPortalSessionFn({
    accessToken,
    returnUrl: `${window.location.origin}/dashboard`,
  });

  if (result.data.url) {
    window.location.href = result.data.url;
  } else {
    throw new Error("Failed to open billing portal");
  }
};
